'use client';

/**
 * DateButton — a styled "pick a date" button over the browser's own picker.
 *
 * The raw <input type="date"> shows "dd/mm/yyyy" with a stock calendar glyph
 * and never matched the fields around it. This keeps the native picker (and
 * its keyboard + screen-reader support) but shows a button that reads like
 * the rest of the planner: "Add a date" until there is one, then "Wed 21 Oct".
 */

import React, { useId, useRef } from 'react';

interface DateButtonProps {
  /** YYYY-MM-DD, or '' / null for none. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Accessible name, e.g. "Leaving on". */
  label: string;
  placeholder?: string;
  min?: string;
  size?: 'sm' | 'md';
  className?: string;
}

export function formatTripDate(iso: string | null | undefined): string | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}

function CalendarGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </svg>
  );
}

export default function DateButton({
  value,
  onChange,
  label,
  placeholder = 'Add a date',
  min,
  size = 'md',
  className = '',
}: DateButtonProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const id = useId();
  const shown = formatTripDate(value);

  const open = () => {
    const el = inputRef.current;
    if (!el) return;
    try {
      // Chromium / Safari 16+ / Firefox 101+.
      (el as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
    } catch {
      el.focus();
    }
  };

  const sm = size === 'sm';
  return (
    <span className={`relative inline-flex ${className}`}>
      <button
        type="button"
        onClick={open}
        aria-label={shown ? `${label}: ${shown}. Change date` : `${label}: ${placeholder}`}
        className={`inline-flex w-full items-center gap-2 rounded-lg border border-solid bg-white text-left transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-tp-brand/15 ${
          shown ? 'border-tp-line-strong text-tp-ink' : 'border-tp-line-strong text-tp-muted'
        } hover:border-tp-line-strong focus-visible:border-tp-brand ${
          sm ? 'h-8 min-h-0 px-2.5 text-[13px]' : 'h-11 px-3 text-[15px]'
        }`}
      >
        <CalendarGlyph className={`flex-none ${sm ? 'h-3.5 w-3.5' : 'h-[18px] w-[18px]'} ${shown ? 'text-tp-brand' : 'text-tp-faint'}`} />
        <span className="truncate">{shown ?? placeholder}</span>
      </button>
      {shown && (
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label={`Clear ${label.toLowerCase()}`}
          className={`absolute top-1/2 flex -translate-y-1/2 items-center justify-center rounded-md text-tp-faint transition-colors hover:bg-tp-surface hover:text-tp-muted ${
            sm ? 'right-0.5 h-7 w-7 min-h-0 min-w-0' : 'right-1.5 h-8 w-8 min-h-0 min-w-0'
          }`}
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      )}
      {/* The real control: invisible, out of the tab order (the button above
          is the focus target), but still what opens the native picker. */}
      <input
        ref={inputRef}
        id={id}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        min={min}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="pointer-events-none absolute bottom-0 left-0 h-px w-px opacity-0"
      />
    </span>
  );
}
